import React, { useEffect, createContext, useCallback, useState } from 'react';
import { addEdge, MarkerType, useEdgesState, useNodesState } from "reactflow";
import { journeyDefinitionSave } from "../services/model/journey-service";
import { debounce } from 'lodash'
import { CHOICE_NODE_TYPE } from "../pages/action-journey-studio/common/const/node-types";

const DEBOUNCE_DELAY = 300; // Set a delay in milliseconds (500ms in this example)


export const WorkflowCanvasContext = createContext("WorkflowContext");

const WorkflowCanvasContextProvider = ({ children }) => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [pendingNodeInternalsUpdate, setPendingNodeInternalsUpdate] = useState(null);

    const [nodeDrawerVisible, setNodeDrawerVisibility] = useState(false);
    const [nodeAttribute, setNodeAttribute] = useState(null);

    const [edgeLabelModalVisible, setEdgeLabelModalVisible] = useState(false);
    const [selectedEdgeConnection, setSelectedEdgeConnection] = useState(null);
    const [edgeLabel, setEdgeLabel] = useState("");

    const [metadata, setMetadata] = useState(null);

    const [selectedStepId, setSelectedStepId] = useState(null);

    const [saveSuccess, setSaveSuccess] = useState(false);
    const [backgroundSyncActive, setBackgroundSyncActive] = useState(false);
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);

    const [deletableChildId, setDeletableChildId] = useState(null);

    const [saveForDelete, setSaveForDelete] = useState(false);

    const [isPreview, setIsPreview] = useState(false);

    const [currentJourneySession, setCurrentJourneySession] = useState({});
    const [currentJourneyConfig, setCurrentJourneyConfig] = useState({});

    const [connectingSource, setConnectingSource] = useState();
    const [connectingTarget, setConnectingTarget] = useState();

    const [workflowHistoryLoader, setWorkflowHistoryLoader] = useState(null);
    const [invalidStates, setInvalidStates] = useState([]);
    const [saveError, setSaveError] = useState(null);

    const onDragStart = (event, nodeData) => {
        event.dataTransfer.setData("application/reactflow", nodeData.nodeType);
        event.dataTransfer.setData('nodeData', JSON.stringify(nodeData));
    };

    const onDragOver = (event) => {
        event.preventDefault(); // Allow dropping by preventing default behavior
    };

    const calculateHandle = (sourcePos, targetPos, targetId) => {
        const dx = sourcePos.x - targetPos.x;
        const dy = sourcePos.y - targetPos.y;
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
        const normalizedAngle = (angle + 360) % 360;

        if ((normalizedAngle >= 202.5 && normalizedAngle < 247.5) || (normalizedAngle >= 247.5 && normalizedAngle < 292.5)) {
            return `target-${targetId}-top`;
        } else if ((normalizedAngle >= 22.5 && normalizedAngle < 67.5) || (normalizedAngle >= 67.5 && normalizedAngle < 112.5)) {
            return `target-${targetId}-bottom`
        } else if (normalizedAngle >= 337.5 || normalizedAngle < 22.5) {
            return `target-${targetId}-right-bottom`;
        } else if (normalizedAngle >= 292.5 && normalizedAngle < 337.5) {
            return `target-${targetId}-right-top`;
        } else if (normalizedAngle >= 112.5 && normalizedAngle < 157.5) {
            return `target-${targetId}-left-bottom`;
        } else if (normalizedAngle >= 157.5 && normalizedAngle < 202.5) {
            return `target-${targetId}-left-top`
        } else {
            return `target-${targetId}-left-top`;
        }
    };

    const calculateHandleSource = (sourcePos, targetPos) => {
        const dx = sourcePos.x - targetPos.x;
        const dy = sourcePos.y - targetPos.y;
        const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
        const normalizedAngle = (angle + 360) % 360;

        if (normalizedAngle >= 337.5 || normalizedAngle < 22.5) {
            return 'left';
        } else if (normalizedAngle >= 22.5 && normalizedAngle < 67.5) {
            return 'left';
        } else if (normalizedAngle >= 67.5 && normalizedAngle < 112.5) {
            return 'right';
        } else if (normalizedAngle >= 112.5 && normalizedAngle < 157.5) {
            return 'right';
        } else if (normalizedAngle >= 157.5 && normalizedAngle < 202.5) {
            return 'right';
        } else if (normalizedAngle >= 202.5 && normalizedAngle < 247.5) {
            return 'right';
        } else if (normalizedAngle >= 247.5 && normalizedAngle < 292.5) {
            return 'left';
        } else if (normalizedAngle >= 292.5 && normalizedAngle < 337.5) {
            return 'left';
        } else {
            return 'right';// Default handle
        }
    };

    const onEdgeUpdateForMove = (event, node) => {
        setEdges((eds) => {
            return eds.map((edge) => {
                const sourceNode = nodes.find((n) => n.id === edge.source);
                const targetNode = nodes.find((n) => n.id === edge.target);

                console.log('edge', edge)
                if (sourceNode && targetNode) {
                    const targetHandleId = calculateHandle(
                        sourceNode.positionAbsolute || sourceNode.position,
                        targetNode.positionAbsolute || targetNode.position,
                        targetNode.id
                    );

                    const sourceHandleClass = calculateHandleSource(
                        sourceNode.positionAbsolute || sourceNode.position,
                        targetNode.positionAbsolute || targetNode.position
                    );
                    let sourceHandleId = edge.sourceHandle.replaceAll('left', '')
                    sourceHandleId = sourceHandleId.replaceAll('right', '')
                    sourceHandleId = sourceHandleId + sourceHandleClass
                    console.log('!!!! sourceHandleId', sourceHandleId)
                    return {
                        ...edge,
                        targetHandle: targetHandleId,
                        sourceHandle: sourceHandleId
                    };
                }
                return edge;
            });
        });
    }

    const onConnect = useCallback(
        (connection) => {

            const sourceNode = nodes.find((node) => node.id === connection.source);

            const edge = {
                ...connection,
                targetHandle: `target-${connection.target}-left-top`,
                type: 'custom',
                markerEnd: {
                    type: MarkerType.Arrow,
                    width: 20,
                    height: 20,
                    color: '#969b9f',
                },
                style: {
                    strokeWidth: 2,
                    stroke: '#969b9f',
                },
            };

            /*
            * Start of maximum one output connection
            * */

            let filteredEdges = edges;

            // if (!sourceNode?.data?.children[sourceNode?.data?.children?.length - 1]?.stateConfig?.choices) {
            // // if (!sourceNode?.data?.children[sourceNode?.data?.children?.length - 1]?.children) {
            //     filteredEdges = edges.filter(edg => edg.source !== connection.source);
            // }

            if (sourceNode && sourceNode.data && sourceNode.data.children) {
                if (!sourceNode?.data?.children[sourceNode?.data?.children?.length - 1]?.stateConfig?.choices) {
                    // if (!sourceNode?.data?.children[sourceNode?.data?.children?.length - 1]?.children) {
                    filteredEdges = edges.filter(edg => edg.source !== connection.source);
                }
            }

            /*
            * End of maximum one output connection
            * */


            setEdges(() => addEdge(edge, filteredEdges));

        }, [nodes, setEdges]
    );

    const openNodeDrawer = (item, parentId) => {

        setNodeAttribute({ ...item, parentId });
        setNodeDrawerVisibility(true);

    };

    const closeNodeDrawer = () => {

        setNodeDrawerVisibility(false);

    }

    const handleNodeConfigChange = e => {

        const { name, value } = e.target;

        const _nodeAttribute = {
            ...nodeAttribute,
            stateConfig: {
                ...nodeAttribute.stateConfig,
                [name]: value,
            }
        }

        setNodeAttribute(_nodeAttribute);
    }

    const handleNodeFullConfigChange = newConfig => {
        const _nodeAttribute = {
            ...nodeAttribute,
            stateConfig: {
                ...nodeAttribute.stateConfig,
                ...newConfig,
            }
        }

        const blockIndex = nodes
            .findIndex(item => item.id === nodeAttribute.parentId);
        const blockNode = nodes[blockIndex]
        blockNode.data.children = blockNode.data.children.map(step => {
            if (step.id === nodeAttribute.id) {
                return {
                    ...step,
                    stateConfig: newConfig
                }
            }
            return step
        })
        nodes[blockIndex] = blockNode
        setNodes([...nodes])
        setNodeAttribute(_nodeAttribute);
    }

    const addNodeAttributeTextVariants = () => {

        let _nodeAttributeTextVariants = [];

        if (nodeAttribute?.stateConfig?.displayTextOptions?.length > 0) {
            _nodeAttributeTextVariants = nodeAttribute.stateConfig.displayTextOptions;
        }

        const _nodeAttribute = {
            ...nodeAttribute,
            stateConfig: {
                ...nodeAttribute.stateConfig,
                displayTextOptions: [
                    ..._nodeAttributeTextVariants,
                    { id: new Date().getMilliseconds() }
                ]
            },
        }

        setNodeAttribute(_nodeAttribute);

    }

    const deleteNodeAttributeTextVariants = id => {

        const _nodeAttributeTextVariants = nodeAttribute?.stateConfig?.displayTextOptions?.filter(nodeAttributeTextVariant => nodeAttributeTextVariant.id !== id);

        const _nodeAttribute = {
            ...nodeAttribute,
            stateConfig: {
                ...nodeAttribute.stateConfig,
                displayTextOptions: _nodeAttributeTextVariants
            }
        }

        setNodeAttribute(_nodeAttribute);

    }

    const nodeAttributeTextVariantOnchange = (e, id) => {

        const { name, value } = e.target;

        const _nodeAttributeTextVariants = nodeAttribute?.stateConfig?.displayTextOptions?.map(nodeAttributeTextVariant => {
            if (nodeAttributeTextVariant.id === id) {
                return {
                    ...nodeAttributeTextVariant,
                    [name]: value
                }
            }
            return nodeAttributeTextVariant;
        });

        const _nodeAttribute = {
            ...nodeAttribute,
            stateConfig: {
                ...nodeAttribute.stateConfig,
                displayTextOptions: _nodeAttributeTextVariants,
            }
        }

        setNodeAttribute(_nodeAttribute)

    }

    const handleDevWebhookConfig = e => {

        const { name, value } = e.target;

        const _nodeAttribute = {
            ...nodeAttribute,
            stateConfig: {
                ...nodeAttribute.stateConfig,
                webhookConfig: {
                    ...nodeAttribute.stateConfig.webhookConfig,
                    [name]: value,
                }
            }
        }

        setNodeAttribute(_nodeAttribute);
    }

    const closeEdgeLabelModal = () => {
        setEdgeLabelModalVisible(false);
    }

    const handleEdgeLabelAdd = () => {
        if (selectedEdgeConnection) {
            // When the user clicks 'OK' in the modal, add the edge with the provided label
            const edge = {
                ...selectedEdgeConnection,
                type: 'custom',
                label: edgeLabel, // Use the input value as the label
                markerEnd: {
                    type: MarkerType.Arrow,
                    width: 20,
                    height: 20,
                    color: '#00A89E',
                },
                style: {
                    strokeWidth: 2,
                    stroke: '#00A89E',
                },
            };

            setEdges((eds) => addEdge(edge, eds));
            setEdgeLabel('');
            setSelectedEdgeConnection(null);

            closeEdgeLabelModal();

        }
    }

    const handleEdgeLabelChange = value => {
        setEdgeLabel(value);
    }


    function transformSchema(initialSchema) {
        console.log('initialSchema', initialSchema)
        const targetSchema = {
            startAt: null,
            states: {}
        };
        const errors = [];
        const errorNodeIds = new Set();

        // Helper function to process children nodes recursively
        function processNode(node, nextNodeId = null, blockId) {
            const { id, nodeType, type, stateConfig } = node;
            stateConfig.blockId = blockId

            if (!stateConfig) {
                errors.push(`Node ${id} is missing stateConfig`);
                errorNodeIds.add(String(id));
                return
            }

            if (stateConfig && nodeType === CHOICE_NODE_TYPE && stateConfig.choices.length === 0) {
                errors.push(`Choice node ${id} has no choices defined`);
                errorNodeIds.add(String(id));
                return
            }

            // Assign stateConfig and next (next only if it exists)
            targetSchema.states[id] = {
                type,
                stateConfig,
                ...(nextNodeId ? { next: nextNodeId } : {})
            };

            if (!targetSchema.startAt) {
                targetSchema.startAt = String(id);
            }
        }

        // Deduplicate nodes by id, keeping the first occurrence (most recently updated via findIndex)
        const seenNodeIds = new Set();
        const uniqueNodes = initialSchema.nodes.filter(node => {
            if (seenNodeIds.has(node.id)) return false;
            seenNodeIds.add(node.id);
            return true;
        });

        // Traverse the nodes and build the target schema
        uniqueNodes.forEach(mainNode => {
            if (mainNode.type === 'StartNode') {
                targetSchema.startAt = mainNode.id
                mainNode.data.stateConfig.blockId = mainNode.id
                targetSchema.states[mainNode.id] = {
                    next: mainNode.data.stateConfig.next,
                    stateConfig: mainNode.data.stateConfig
                };
            } else {
                const children = mainNode.data.children;

                children.forEach((childNode, index) => {
                    const nextChild = children[index + 1] ? String(children[index + 1].id) : null;
                    processNode(childNode, nextChild, mainNode.id);
                });
            }
        });

        // Handle edges to connect different blocks
        initialSchema.edges.forEach(edge => {
            const { source, target } = edge;
            let { sourceHandle } = edge;

            const firstTarget = initialSchema.nodes
                .find(node => node.id === target)
            if (!firstTarget) {
                errors.push(`Edge target node not found: ${target}`);
                // Try to find and mark children from target block if it exists
                const targetNode = initialSchema.nodes.find(n => n.id === target);
                if (targetNode && targetNode.data && targetNode.data.children) {
                    targetNode.data.children.forEach(child => errorNodeIds.add(String(child.id)));
                }
                return
            }

            // Check if target has children (skip note nodes)
            if (!firstTarget.data || !firstTarget.data.children || firstTarget.data.children.length === 0) {
                errors.push(`Target node ${target} has no children`);
                // Mark the target block's children if any
                if (firstTarget.data && firstTarget.data.children) {
                    firstTarget.data.children.forEach(child => errorNodeIds.add(String(child.id)));
                }
                return
            }

            const firstTargetChild = firstTarget.data.children[0];

            sourceHandle = sourceHandle.replaceAll('-right', '').replaceAll('-left', '')
            if (sourceHandle.startsWith('source-condition')) {
                const splits = sourceHandle.replaceAll('source-condition-', '').split('-')
                const stepId = +splits[0]
                const choicePathId = +splits[1]

                if (!targetSchema.states[stepId]) {
                    errors.push(`Source state not found for condition: ${stepId}`);
                    errorNodeIds.add(String(stepId));
                    return
                }

                const choice = targetSchema.states[stepId].stateConfig.choices
                    ?.find(choice => choice.id === choicePathId)
                if (choice) {
                    choice.next = String(firstTargetChild.id);
                }
            } else if (sourceHandle.startsWith('source-else-condition')) {
                const stepId = sourceHandle.replaceAll('source-else-condition-', '')
                if (targetSchema.states[stepId]) {
                    targetSchema.states[stepId].stateConfig.next = String(firstTargetChild.id);
                }
            } else if (sourceHandle === 'start') {
                if (targetSchema.states[source]) {
                    targetSchema.states[source].stateConfig.next = String(firstTargetChild.id);
                }
            } else {
                const node = initialSchema.nodes
                    .find(node => node.id === source)
                if (!node) {
                    const targetNode = initialSchema.nodes.find(n => n.id === target);
                    const targetLabel = targetNode?.data?.label || target;
                    errors.push(`Invalid connection: Source block ${source} not found. Connection to "${targetLabel}" is broken. Please delete this edge.`);

                    // Mark the target node's children since the source is missing
                    if (targetNode && targetNode.data && targetNode.data.children) {
                        console.log(`Marking target node ${target} children as invalid:`, targetNode.data.children.map(c => c.id));
                        targetNode.data.children.forEach(child => errorNodeIds.add(String(child.id)));
                    }
                    // Also check if the sourceHandle contains a step ID we can extract
                    const handleMatch = sourceHandle.match(/source-(\d+)-(\d+)/);
                    if (handleMatch) {
                        const stepId = handleMatch[1];
                        console.log(`Extracted step ID from handle: ${stepId}`);
                        errorNodeIds.add(String(stepId));
                    }
                    return
                }
                if (node && node.data && node.data.children) {
                    const lastSourceChild = node.data.children.slice(-1)[0];
                    if (targetSchema.states[lastSourceChild.id]) {
                        targetSchema.states[lastSourceChild.id].next = String(firstTargetChild.id);
                    }
                } else if (targetSchema.states[+node.id]) {
                    targetSchema.states[+node.id].next = String(firstTargetChild.id);
                }
            }
        });

        // Throw error if any issues were found
        if (errors.length > 0) {
            const errorNodeIdsArray = Array.from(errorNodeIds);
            console.log('Error Node IDs:', errorNodeIdsArray);
            const error = new Error(`Workflow validation failed:\n${errors.join('\n')}`);
            error.errorNodeIds = errorNodeIdsArray;
            throw error;
        }

        return targetSchema;
    }

    function validateNextReferences(stmDefinition) {
        try {
            if (!stmDefinition || !stmDefinition.states) return [];
            const valid = new Set(Object.keys(stmDefinition.states).map(String));
            const invalid = new Set();
            for (const [id, state] of Object.entries(stmDefinition.states)) {
                const nextVals = [];
                if (state.next !== undefined && state.next !== null) nextVals.push(String(state.next));
                const sc = state.stateConfig || {};
                if (sc.next !== undefined && sc.next !== null) nextVals.push(String(sc.next));
                if (Array.isArray(sc.choices)) {
                    for (const ch of sc.choices) {
                        if (ch && ch.next !== undefined && ch.next !== null) nextVals.push(String(ch.next));
                    }
                }
                for (const n of nextVals) {
                    if (n && !valid.has(n)) {
                        invalid.add(String(id));
                        break;
                    }
                }
            }
            return Array.from(invalid);
        } catch (e) {
            return [];
        }
    }

    const saveWorkflow = async (autoSave = true) => {
        if (!metadata || !metadata.appId || !metadata.journeyId || nodes.length === 0) {
            return
        }
        setBackgroundSyncActive(true)
        try {
            setSaveSuccess(false)
            setSaveError(null)
            setInvalidStates([])

            // Validate and clean up edges before transformation
            const validNodeIds = new Set(nodes.map(n => n.id));
            const invalidEdges = edges.filter(edge => {
                const sourceExists = validNodeIds.has(edge.source);
                const targetExists = validNodeIds.has(edge.target);
                if (!sourceExists || !targetExists) {
                    console.warn('Invalid edge found:', {
                        edge,
                        sourceExists,
                        targetExists,
                        availableNodes: Array.from(validNodeIds)
                    });
                    return true;
                }
                return false;
            });

            if (invalidEdges.length > 0) {
                console.error('Found invalid edges that reference non-existent nodes:', invalidEdges);
                const errorMsg = `Found ${invalidEdges.length} invalid connection(s) referencing deleted blocks. These will be automatically removed.`;

                // Auto-cleanup: Remove invalid edges
                const cleanedEdges = edges.filter(edge => {
                    return validNodeIds.has(edge.source) && validNodeIds.has(edge.target);
                });

                console.log('Cleaning up edges. Before:', edges.length, 'After:', cleanedEdges.length);
                setEdges(cleanedEdges);

                // Show warning but allow save to continue with cleaned edges
                setSaveError(errorMsg + ' Workflow will be saved with cleaned connections.');
                setTimeout(() => setSaveError(null), 5000);
            }

            const stmDefinition = transformSchema({
                nodes,
                edges: invalidEdges.length > 0 ? edges.filter(edge => validNodeIds.has(edge.source) && validNodeIds.has(edge.target)) : edges
            })
            console.log("stmDefinition", stmDefinition)

            // const invalids = validateNextReferences(stmDefinition)
            // setInvalidStates(invalids)
            // if (invalids.length > 0) {
            //     setBackgroundSyncActive(false)
            //     return
            // }

            await journeyDefinitionSave(
                metadata.appId,
                metadata.journeyId,
                stmDefinition,
                {
                    nodes,
                    edges,
                },
                autoSave
            );
            setSaveSuccess(true)

            if (!autoSave && workflowHistoryLoader) {
                setTimeout(async () => {
                    console.log('workflowHistoryLoader type:', typeof workflowHistoryLoader)
                    try {
                        if (typeof workflowHistoryLoader === 'function') {
                            await workflowHistoryLoader()
                        } else {
                            console.error('workflowHistoryLoader is not a function:', typeof workflowHistoryLoader)
                        }
                    } catch (error) {
                        console.error('Error executing workflowHistoryLoader:', error)
                    }
                }, 5000)
            }

        } catch (e) {
            console.error('saved workflow failed', e)
            setSaveError(e.message || 'Failed to save workflow')
            // Highlight error nodes if available
            if (e.errorNodeIds && Array.isArray(e.errorNodeIds)) {
                console.log('Setting invalid states:', e.errorNodeIds)
                setInvalidStates(e.errorNodeIds)
            } else {
                console.log('No errorNodeIds found in error object')
            }
        }
        setBackgroundSyncActive(false)
    }

    const debouncedSaveWorkflow = useCallback(debounce(saveWorkflow, DEBOUNCE_DELAY), [metadata]);

    const debouncedSetSaveForDelete = useCallback(debounce(() => { setSaveForDelete(true); }, DEBOUNCE_DELAY), []);

    // Set auto-save function
    const setAutoSave = (enabled) => {
        setAutoSaveEnabled(enabled);
    };

    // Auto-save effect - runs when autoSaveEnabled is true and nodes or edges change
    useEffect(() => {
        if (autoSaveEnabled && nodes.length > 0 && metadata?.appId && metadata?.journeyId) {
            debouncedSaveWorkflow();
        }
    }, [nodes, edges, autoSaveEnabled, metadata]);

    useEffect(() => {
        if (saveForDelete && autoSaveEnabled) {
            saveWorkflow()
        }
        setSaveForDelete(false)
    }, [
        saveForDelete
    ]);

    const handleDelete = (id, childId = null) => {

        let _childId = childId;
        if (deletableChildId) {
            _childId = deletableChildId
        }

        if (_childId) {
            const selectedBlock = nodes.find(item => item.id === id);
            if (!selectedBlock) {
                console.warn('Block not found for deletion:', id);
                return;
            }

            // Check if the deleted child is the last child (the one with the source handle)
            const oldChildren = selectedBlock.data.children;
            const oldLastChild = oldChildren[oldChildren.length - 1];
            const deletedChild = oldChildren.find(item => item.id === _childId);

            selectedBlock.data.children = selectedBlock.data.children.filter(item => item.id !== _childId);
            if (selectedBlock.data.children.length > 0) {
                const newChildren = selectedBlock.data.children;
                const newLastChild = newChildren[newChildren.length - 1];

                // If the deleted child is a condition (choice) node, remove all outgoing edges
                if (deletedChild && deletedChild.nodeType === 'choice') {
                    setEdges(eds => eds.filter(edge => edge.source !== id));
                }
                // Otherwise, if the last child changed, update edge sourceHandle to point to new last child
                else if (oldLastChild && newLastChild && String(oldLastChild.id) !== String(newLastChild.id)) {
                    setEdges(eds => eds.map(edge => {
                        if (edge.source !== id) return edge;
                        const sourceHandle = edge.sourceHandle;
                        if (!sourceHandle) return edge;
                        const prefix = `source-${id}-`;
                        if (sourceHandle.startsWith(prefix) && !sourceHandle.includes('condition')) {
                            const remaining = sourceHandle.substring(prefix.length);
                            const lastHyphenIndex = remaining.lastIndexOf('-');
                            if (lastHyphenIndex === -1) return edge;
                            const childIdStr = remaining.substring(0, lastHyphenIndex);
                            const pos = remaining.substring(lastHyphenIndex + 1);
                            if (String(childIdStr) === String(oldLastChild.id)) {
                                const newSourceHandle = `source-${id}-${newLastChild.id}-${pos}`;
                                console.log(`Delete: Moving edge from ${sourceHandle} to ${newSourceHandle}`);
                                return { ...edge, sourceHandle: newSourceHandle };
                            }
                        }
                        return edge;
                    }));
                }

                const findIndexBlock = nodes.findIndex(item => item.id === id);
                nodes[findIndexBlock] = selectedBlock;
                setNodes([...nodes]);
            } else {
                // If no children left, delete the entire block and its edges
                const updateNodes = nodes.filter(item => item.id !== id);
                setEdges(eds => eds.filter((edge) => edge.source !== id && edge.target !== id));
                setNodes(updateNodes);
            }
            setDeletableChildId(null)
        } else {
            // Delete node and all connected edges
            const updatedData = nodes.filter(item => item.id !== id);
            setEdges(eds => eds.filter((edge) => edge.source !== id && edge.target !== id));
            setNodes(updatedData)
        }
        debouncedSetSaveForDelete()
    }

    return (
        <WorkflowCanvasContext.Provider
            value={{
                nodes,
                setNodes,
                edges,
                setEdges,
                onNodesChange,
                onEdgesChange,
                nodeDrawerVisible,
                edgeLabelModalVisible,
                onConnect,
                nodeAttribute,
                setNodeAttribute,
                setMetadata,
                selectedStepId,
                setSelectedStepId,
                metadata,
                saveSuccess,
                backgroundSyncActive,
                deletableChildId,
                setDeletableChildId,
                handleDelete,
                handleDevWebhookConfig,
                openNodeDrawer,
                onDragStart,
                onDragOver,
                closeNodeDrawer,
                handleNodeConfigChange,
                handleNodeFullConfigChange,
                handleEdgeLabelAdd,
                handleEdgeLabelChange,
                closeEdgeLabelModal,
                addNodeAttributeTextVariants,
                deleteNodeAttributeTextVariants,
                nodeAttributeTextVariantOnchange,
                isPreview,
                setIsPreview,
                currentJourneySession,
                setCurrentJourneySession,
                currentJourneyConfig,
                setCurrentJourneyConfig,
                onEdgeUpdateForMove,
                connectingSource,
                setConnectingSource,
                connectingTarget,
                setConnectingTarget,
                saveWorkflow,
                setAutoSave,
                autoSaveEnabled,
                setWorkflowHistoryLoader
                ,
                invalidStates,
                saveError,
                setSaveError,
                pendingNodeInternalsUpdate,
                setPendingNodeInternalsUpdate
            }}
        >
            {children}
        </WorkflowCanvasContext.Provider>
    );
}

export default WorkflowCanvasContextProvider;
